/**
 * markerFactory.js
 * Creates L.divIcon instances from inline SVG for each entity type.
 * The map engine never imports entity-specific code — pages pass a pre-built icon
 * via the marker object, or call createMarkerIcon(type, color) themselves.
 */
import L from 'leaflet';
import { MARKER_TYPES } from '../constants/markerTypes';
import { STATUS_COLORS } from '../constants/statusColors';

// ─── SVG icon paths (Material Design icon subsets, optimised) ──────────────

const ICON_PATHS = {
  [MARKER_TYPES.USER]: `
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="white"/>`,

  [MARKER_TYPES.HOSPITAL]: `
    <path d="M19 3H5c-1.1 0-1.99.9-1.99 2L3 19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-1 11h-4v4h-2v-4H8v-2h4V8h2v4h4v2z" fill="white"/>`,

  [MARKER_TYPES.SNAKE_REPORT]: `
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="white"/>`,

  [MARKER_TYPES.RESCUER]: `
    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 4l5 2.18V11c0 3.5-2.33 6.79-5 7.93C9.33 17.79 7 14.5 7 11V7.18L12 5z" fill="white"/>`,

  [MARKER_TYPES.SNAKE_BITE]: `
    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="white"/>`,

  [MARKER_TYPES.EMERGENCY]: `
    <path d="M12 2L1 21h22L12 2zm0 3.5l8.5 15.5H3.5L12 5.5zM11 10v4h2v-4h-2zm0 6v2h2v-2h-2z" fill="white"/>`,

  [MARKER_TYPES.CONTROL_ROOM]: `
    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" fill="white"/>`,
};

// ─── Pin SVG builder ────────────────────────────────────────────────────────

function buildPinSvg(color, iconPath) {
  return `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 48" width="36" height="48">
  <defs>
    <filter id="pin-shadow" x="-20%" y="-10%" width="140%" height="130%">
      <feDropShadow dx="0" dy="2" stdDeviation="1.5" flood-color="rgba(0,0,0,0.12)"/>
    </filter>
  </defs>
  <!-- Pin body -->
  <path d="M18 0C8.06 0 0 8.06 0 18c0 12.77 15.75 28.5 17.12 29.96a1.26 1.26 0 0 0 1.76 0C20.25 46.5 36 30.77 36 18 36 8.06 27.94 0 18 0z"
        fill="${color}" filter="url(#pin-shadow)"/>
  <!-- White circle background for icon -->
  <circle cx="18" cy="17" r="10" fill="rgba(255,255,255,0.18)"/>
  <!-- Icon centred in pin -->
  <svg x="6" y="5" width="24" height="24" viewBox="0 0 24 24">
    ${iconPath}
  </svg>
</svg>`;
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Create a Leaflet divIcon for the given marker type and color.
 * @param {string} type   - MARKER_TYPES constant
 * @param {string} color  - hex color string
 * @returns {L.DivIcon}
 */
export function createMarkerIcon(type, color) {
  const path   = ICON_PATHS[type] || ICON_PATHS[MARKER_TYPES.USER];
  const svgHtml = buildPinSvg(color || STATUS_COLORS.NEUTRAL, path);

  return L.divIcon({
    html:        svgHtml,
    className:   'sarpa-map-marker',
    iconSize:    [36, 48],
    iconAnchor:  [18, 48],
    popupAnchor: [0, -50],
  });
}

/**
 * Convenience: get the default icon for a layer type.
 * Pages can override by passing marker.icon directly.
 */
export function defaultIcon(layerId, color) {
  const typeMap = {
    users:        MARKER_TYPES.USER,
    hospitals:    MARKER_TYPES.HOSPITAL,
    snakeReports: MARKER_TYPES.SNAKE_REPORT,
    rescuers:     MARKER_TYPES.RESCUER,
    snakeBites:   MARKER_TYPES.SNAKE_BITE,
    emergency:    MARKER_TYPES.EMERGENCY,
    controlRoom:  MARKER_TYPES.CONTROL_ROOM,
  };
  const type = typeMap[layerId] || MARKER_TYPES.USER;
  return createMarkerIcon(type, color);
}
